// frontend/modules/internal-account/stores/internal-account-store.ts
import { create } from 'zustand';

// Define the internal account DTO interface
export interface InternalAccountDTO {
    branch: string;
    glNo: string;
    acNo: string;
    acName: string;
    acStatus: 'A' | 'C'; // A = Active, C = Closed
}

// Define the store state interface
interface InternalAccountState {
    accounts: InternalAccountDTO[];
    filteredAccounts: InternalAccountDTO[];
    isLoading: boolean;
    error: string | null;
    searchTerm: string;
    
    // Active account state for editing
    activeAccount: InternalAccountDTO | null;
    isEditMode: boolean;
    
    // Actions
    setAccounts: (accounts: InternalAccountDTO[]) => void;
    setFilteredAccounts: (accounts: InternalAccountDTO[]) => void;
    setIsLoading: (isLoading: boolean) => void;
    setError: (error: string | null) => void;
    setSearchTerm: (searchTerm: string) => void;
    setActiveAccount: (account: InternalAccountDTO | null) => void;
    setIsEditMode: (isEditMode: boolean) => void;
    resetActiveAccount: () => void;
    filterAccounts: () => void;
}

export const useInternalAccountStore = create<InternalAccountState>((set, get) => ({
    accounts: [],
    filteredAccounts: [],
    isLoading: false,
    error: null,
    searchTerm: '',
    
    activeAccount: null,
    isEditMode: false,
    
    // Actions
    setAccounts: (accounts) => {
        set({ accounts });
        get().filterAccounts();
    },
    
    setFilteredAccounts: (filteredAccounts) => set({ filteredAccounts }),
    
    setIsLoading: (isLoading) => set({ isLoading }),
    
    setError: (error) => set({ error }),
    
    setSearchTerm: (searchTerm) => {
        set({ searchTerm });
        get().filterAccounts();
    },
    
    setActiveAccount: (account) => set({ 
        activeAccount: account,
        isEditMode: account !== null
    }),
    
    setIsEditMode: (isEditMode) => set({ isEditMode }),
    
    resetActiveAccount: () => set({ 
        activeAccount: null,
        isEditMode: false
    }),
    
    filterAccounts: () => {
        const { accounts, searchTerm } = get();
        
        if (!searchTerm.trim()) {
            set({ filteredAccounts: accounts });
            return;
        }
        
        const lowercaseSearchTerm = searchTerm.toLowerCase();
        const filtered = accounts.filter(account => 
            account.acName.toLowerCase().includes(lowercaseSearchTerm) ||
            account.glNo.toLowerCase().includes(lowercaseSearchTerm) ||
            account.acNo.toLowerCase().includes(lowercaseSearchTerm)
        );
        
        set({ filteredAccounts: filtered });
    }
}));
